import React, { useState, useEffect, useRef } from 'react';
import { MenuIcon } from './Icons';
import NotificationDropdown from './NotificationDropdown';
import CurrencySelect from './CurrencySelect';
import { useLanguage, LANGUAGE_OPTIONS } from '../translations/LanguageContext';
import { getOnlineUsers } from '../utils/presence';

const PAGE_KEYS = {
  dashboard: 'nav_dashboard', inventory: 'nav_inventory', customers: 'nav_customers',
  myStock: 'nav_my_stock', containers: 'nav_containers', requests: 'nav_requests',
  employees: 'nav_employees', scan: 'nav_scan', settings: 'nav_settings',
  invoices: 'nav_invoices', payments: 'nav_payments', pricing: 'nav_pricing',
  priceQuotes: 'nav_price_quotes', selectionOrders: 'nav_selection_orders',
  tasks: 'nav_tasks',
  auditLog: 'nav_audit_log',
};

function OnlineIndicator() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let active = true;
    const poll = () => getOnlineUsers().then(u => { if (active) setUsers(u); });
    poll();
    const iv = setInterval(poll, 30000);
    return () => { active = false; clearInterval(iv); };
  }, []);

  

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const count = users.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
          borderRadius: 16, border: '1px solid var(--border)',
          background: open ? 'var(--bg3)' : 'transparent', cursor: 'pointer',
          color: 'var(--text1)', fontSize: 11, fontWeight: 500,
        }}
        title={`${count} user${count !== 1 ? 's' : ''} online`}
      >
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: count > 0 ? '#3fb950' : '#484f58',
          display: 'inline-block',
          animation: count > 0 ? 'pulse-dot 2s infinite' : 'none',
        }} />
        {count}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 100,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: 220, maxHeight: 320, overflow: 'auto',
        }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 600, color: 'var(--text3)' }}>
            Live Users ({count})
          </div>
          {count === 0 ? (
            <div style={{ padding: '16px 12px', fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>No one online</div>
          ) : (
            users.map(u => {
              const isCustomer = u.user_role === 'customer' || u.user_role === 'business';
              const ago = Math.round((Date.now() - new Date(u.last_seen).getTime()) / 1000);
              const agoText = ago < 60 ? 'now' : `${Math.round(ago / 60)}m`;
              return (
                <div key={u.user_id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3fb950', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.user_name}</div>
                    <div style={{ fontSize: 9, color: 'var(--text3)' }}>
                      {isCustomer ? 'Customer' : (u.user_role || '').replace(/_/g, ' ')}
                    </div>
                  </div>
                  <span style={{ fontSize: 9, color: 'var(--text3)', flexShrink: 0 }}>{agoText}</span>
                </div>
              );
            })
          )}
        </div>
      )}


    </div>
  );
}

function Topbar({ page, user, currency, setCurrency, onMenuClick }) {
  const { t, lang, changeLanguage } = useLanguage();
  const isAdmin = user?.role === 'admin' || user?.role === 'head_admin';

  return (
    <header className="topbar">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          className="btn btn-secondary btn-sm mobile-menu-btn"
          onClick={onMenuClick}
          style={{ padding: 8 }}
        >
          <MenuIcon />
        </button>
        <h1 className="page-title">
          {t(PAGE_KEYS[page] || page)}
        </h1>
      </div>
      <div className="topbar-right">
        {isAdmin && <OnlineIndicator />}
        <NotificationDropdown user={user} />
        {/* Language Selector */}
        <select
          className="topbar-select"
          value={lang}
          onChange={e => changeLanguage(e.target.value)}
        >
          {LANGUAGE_OPTIONS.map(l => (
            <option key={l.code} value={l.code}>{l.name}</option>
          ))}
        </select>

        {/* Currency Selector */}
        <CurrencySelect value={currency} onChange={setCurrency} className="topbar-select" />
      </div>
    </header>
  );
}

export default Topbar;
